import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import styles from "./SearchAll.module.scss";
import InputForms from "../../UiComponents/forms/InputForms";
import { normalize } from "../../../utils/fonctionSearch";
import Axios from "../../../_services/caller.service";
import { decodedB64 } from "../../../CustomHooks/DecodedBase64";

const SERIAL_LABELS = {
  Bone_Serial: "CO",
  Bone_Soundcard_Serial: "Carte son CO",
  Headphones_Serial: "Casque",
  Headphones_Soundcard_Serial: "Carte son Casque",
  Inserts_Serial: "Inserts",
  Inserts_Soundcard_Serial: "Carte son Inserts",
  Operator_Serial: "Operator",
  Operator_Soundcard_Serial: "Carte son Operator",
  Speakers_MicrophoneSerial: "Micro Champ Libre",
  Headphones_MicrophoneSerial: "Micro Casque",
  Inserts_MicrophoneSerial: "Micro Inserts",
};

// Checks if ALL words in the search match at least one field
const multiWordMatch = (fields, searchTerms) =>
  searchTerms.every((term) =>
    fields.some((f) => normalize(f).includes(term))
  );

const SearchAll = ({
  allUser,
  allCabines,
  changeOwnerIdWithSelect,
  changeCabIdWithSelect,
  ownerId,
  infosCabId,
}) => {
  const [search, setSearch] = useState("");
  const { t } = useTranslation();
  const [isSearching, setIsSearching] = useState(false);
  const [serialsMap, setSerialsMap] = useState({});
  const containerRef = useRef(null);

  // Fetch serial numbers once on mount
  useEffect(() => {
    Axios.get("/allCabinesSerials/")
      .then((res) => {
        if (res.data?.data) {
          // Decode base64 values
          const decoded = {};
          for (const [cabId, serials] of Object.entries(res.data.data)) {
            decoded[cabId] = {};
            for (const [fname, fval] of Object.entries(serials)) {
              try {
                decoded[cabId][fname] = decodedB64(fval);
              } catch {
                decoded[cabId][fname] = fval;
              }
            }
          }
          setSerialsMap(decoded);
        }
      })
      .catch(() => {});
  }, []);

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsSearching(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const searchTerms = normalize(search)
    .split(" ")
    .filter((t) => t.length > 0);

  const hasSearch = searchTerms.length > 0;

  // Filter users — multi-word match
  const filteredUser = hasSearch
    ? allUser.filter((u) =>
        multiWordMatch(
          [u.display_name, u.user_email, u.ID.toString(), u.user_login],
          searchTerms
        )
      )
    : [];

  // Filter cabines — match on name, id, AND serial numbers
  const filteredCabines = hasSearch
    ? allCabines
        .map((c) => {
          const cabSerials = serialsMap[c.id] || {};
          const serialFields = Object.entries(cabSerials).map(
            ([fname, val]) => val
          );
          const allFields = [c.cabname, c.id.toString(), ...serialFields];

          const match = multiWordMatch(allFields, searchTerms);
          if (!match) return null;

          // Check which serial matched for display
          const matchedSerials = [];
          for (const [fname, val] of Object.entries(cabSerials)) {
            if (
              searchTerms.some((t) => normalize(val).includes(t))
            ) {
              matchedSerials.push({ label: SERIAL_LABELS[fname], value: val });
            }
          }

          return { ...c, matchedSerials };
        })
        .filter(Boolean)
    : [];

  const handleSelectUser = (userId) => {
    changeOwnerIdWithSelect({ target: { value: userId } });
    setSearch("");
    setIsSearching(false);
  };

  const handleSelectCabine = (cabId) => {
    changeCabIdWithSelect({ target: { value: cabId } });
    setSearch("");
    setIsSearching(false);
  };

  return (
    <div className={styles.containerSearchAll} ref={containerRef}>
      <InputForms
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setIsSearching(true);
        }}
        placeholder={t("superadmin_ui.search_all_placeholder")}
        id={styles.searchAll}
      />

      {isSearching && hasSearch && (
        <div className={styles.results}>
          <div className={styles.group}>
            <p className={styles.groupTitle}>
              {t("superadmin_ui.users_search_count", { count: filteredUser.length })}
            </p>
            <div className={styles.resultList}>
              {filteredUser.length === 0 ? (
                <div className={styles.noResult}>{t("superadmin_ui.no_result")}</div>
              ) : (
                filteredUser.slice(0, 20).map((u) => (
                  <div
                    key={u.ID}
                    className={styles.resultItem}
                    onClick={() => handleSelectUser(u.ID)}
                  >
                    <span className={styles.resultName}>
                      {u.display_name || u.user_login}
                    </span>
                    <span className={styles.resultSub}>{u.user_email}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className={styles.group}>
            <p className={styles.groupTitle}>
              {t("superadmin_ui.cabines_search_count", { count: filteredCabines.length })}
            </p>
            <div className={styles.resultList}>
              {filteredCabines.length === 0 ? (
                <div className={styles.noResult}>{t("superadmin_ui.no_result")}</div>
              ) : (
                filteredCabines.slice(0, 20).map((c) => (
                  <div
                    key={c.id}
                    className={styles.resultItem}
                    onClick={() => handleSelectCabine(c.id)}
                  >
                    <span className={styles.resultName}>
                      {c.cabname}
                    </span>
                    <span className={styles.resultSub}>ID: {c.id}</span>
                    {c.matchedSerials?.length > 0 &&
                      c.matchedSerials.map((s, i) => (
                        <span key={i} className={styles.serialMatch}>
                          {s.label}: {s.value}
                        </span>
                      ))}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchAll;